import { find } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import dayjs from "lib/dayjs";

import {
  setCurrentCategory,
  setCurrentSearchTerm,
  setCurrentSource,
  setCurrentSubCategory,
  setCurrentTimeframe,
  setEndDate,
  setIsActive,
  setPreviousPath,
  setSourceCategories,
  setStartDate,
} from "../context";
import { getValidDate } from "../../../utils/date-validation.util";
import {
  biasCategories,
  databaseCategories,
  getDatabaseCategories,
  getCategories,
  getSources,
  frequencies,
  sources,
  publishersArray
} from "../data/search-fields.data";
import { useSearchCredentials } from "features/Group/hooks";

const defaultSource = find(sources, { default: true });
const defaultTimeframe = find(frequencies, { default: true });

export function useSourceSearch({ fetchCredentials = false } = {}) {
  const { credentials } = useSearchCredentials({ fetchOnMount: fetchCredentials });
  const {
    currentCategory,
    currentSearchTerm,
    currentSource,
    currentSubCategory,
    currentTimeframe,
    endDate,
    isActive,
    previousPath,
    sourceCategories,
    subCategories,
    subPublishers,
    startDate,
  } = useSelector((state) => state.search);  
  const { isHomeschool } = useSelector((state) => state.account);

  const { search, source, category, academiaSearch } = useParams();
  let [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const peerreviewed = pathname.includes("/peer-reviewed");
  
  const categories = useMemo(() => getCategories(isHomeschool), [isHomeschool]);
  const currentSources = useMemo(() => getSources(credentials), [credentials]);

  function handleActiveStateChange(state) {
    dispatch(setIsActive(state));
  }

  const handleSourceCategoryChange = useCallback(
    (source) => {
      if (source === "rating") {
        return biasCategories;
      } else if (source === "databases") {
        return getDatabaseCategories(credentials);
      } else if (source === "partner-publishers") {
        return publishersArray;
      }
      return categories;
    },
    [categories, credentials]
  );

  function getSource(key) {
    return find(sources, { key });
  }

  function getCategory(key) {    
    if (currentSource === "rating") {
      return find(biasCategories, { key });
    } else if (currentSource === "databases") {
      return find(databaseCategories, { key });
    } else if( currentSource === "partner-publishers") {
      return find(publishersArray, { key });
    }
    return find(categories, { key });
  }

  function getTimeframe(key) {
    return find(frequencies, { key });
  }

  function getSubCategory(key) {
    return find(subCategories, { key });
  }

  function buildSearchUrl({
    query,
    source = defaultSource.key,
    category = null,
    date = defaultTimeframe.key,
    subCategory = null,
    endDate = null,
    startDate = null,
  } = {}) {
    if (date == null || source == null) return;
    let filterQuery = "";
    if (date !== defaultTimeframe.key) {
      const timeframe = find(frequencies, { key: date });
      filterQuery = `?pubDate=${timeframe.label}`;
      if (timeframe.key === "custom-date") {
        if (startDate && endDate && getValidDate(startDate) !== null && getValidDate(endDate) !== null) {
          const end = dayjs(endDate).format("MMM DD, YYYY");
          const start = dayjs(startDate).format("MMM DD, YYYY");
          filterQuery = `${filterQuery}&dateRange=${start} - ${end}`;
        }
      }
    }
    const encodedQuery = encodeURI(decodeURI(query));

    if (encodedQuery === "") return;

    if (source === "categories") {
      if (category) {
        const catObj = find(categories, { key: category });
        const subCatObj = find(subCategories, { key: subCategory });
        return `/search/${encodedQuery}/${source}/${catObj.label}${
          subCatObj ? `/${subCatObj.label}` : ""
        }${filterQuery}`;
      } else {
        return `/search/${encodedQuery}${filterQuery}`;
      }
    } else if (source === "rating") {
      if (category) {
        const categoryLabel = find(biasCategories, { key: category }).label;
        return `/search/${encodedQuery}/${source}/${categoryLabel}${filterQuery}`;
      } else {
        return `/search/${encodedQuery}/${source}${filterQuery}`;
      }
    } else if (source === "databases") {
      if (category) {
        const categoryLabel = find(databaseCategories, { key: category })?.label;
        if (!categoryLabel) return `/search/${encodedQuery}/${source}${filterQuery}`;
        return `/search/${encodedQuery}/${source}/${categoryLabel}${filterQuery}`;
      } else {
        return `/search/${encodedQuery}/${source}${filterQuery}`;
      }
    }else if (source === "partner-publishers") {      
      if (category) {        
        const categoryLabel = find(publishersArray, { key: category }).label;
        return `/search/${encodedQuery}/${source}/${categoryLabel}${filterQuery}`;
      } else {
        return `/search/${encodedQuery}/${source}${filterQuery}`;
      }
    } else {
      return `/search/${encodedQuery}/${source}${filterQuery}`;
    }
  }
  useEffect(() => {
    if (search == null) {
      dispatch(setCurrentSearchTerm(""));
    } else {
      dispatch(setCurrentSearchTerm(search));
    }
  }, [search, dispatch]);

  useEffect(() => {
    if (source == null) {
      dispatch(setCurrentSource(defaultSource.key));
      dispatch(setSourceCategories(categories));
    } else if (source === "rating") {
      dispatch(setCurrentSource(source));
      dispatch(setSourceCategories(biasCategories));
      dispatch(setCurrentSubCategory(null));
    } else if (source === "partner-publishers") {
      dispatch(setCurrentSource(source));
      dispatch(setSourceCategories(publishersArray));
      dispatch(setCurrentSubCategory(null));
    } else if (source === "databases") {
      dispatch(setCurrentSource(source));
      dispatch(setSourceCategories(getDatabaseCategories(credentials)));
      dispatch(setCurrentSubCategory(null));
    } else {
      dispatch(setCurrentSource(source));
      dispatch(setSourceCategories(categories));
    }
  }, [source, categories, credentials, dispatch]);

  useEffect(() => {
    if (category != null) {
      const currentCategory = currentSource === "partner-publishers" ? find(publishersArray, { label: category }) : find(sourceCategories, { label: category });
      if (currentCategory != null) {
        if (currentCategory.key === "academia") {
          const subCategory = find(subCategories, { label: academiaSearch });
          if (subCategory != null) {
            dispatch(setCurrentSubCategory(subCategory.key));
          } else {
            dispatch(setCurrentSubCategory(null));
          }
          dispatch(setCurrentCategory(currentCategory.key));
        } else {
          dispatch(setCurrentCategory(currentCategory.key));
          dispatch(setCurrentSubCategory(null));
        }
      } else {
        dispatch(setCurrentCategory(null));
        dispatch(setCurrentSubCategory(null));
      }
    } else {
      dispatch(setCurrentCategory(null));
      dispatch(setCurrentSubCategory(null));
    }
  }, [category, academiaSearch, sourceCategories, subCategories, currentSource, dispatch]);

  useEffect(() => {
    const pubDate = searchParams.get("pubDate");
    const dateRange = searchParams.get("dateRange");

    const filteredTimeframe = find(frequencies, { label: pubDate });
    if (filteredTimeframe != null) {
      dispatch(setCurrentTimeframe(filteredTimeframe.key));
      if (
        filteredTimeframe.key === "custom-date" &&
        searchParams.has("dateRange")
      ) {
        const range = dateRange.split(" - ");
        dispatch(setStartDate(dayjs(range[0])));
        dispatch(setEndDate(dayjs(range[1])));
      }
    } else {
      dispatch(setCurrentTimeframe(defaultTimeframe.key));
      dispatch(setEndDate(null));
      dispatch(setStartDate(null));
    }
  }, [searchParams, dispatch]);

  return {
    buildSearchUrl,
    currentCategory,
    currentSearchTerm,
    currentSource,
    currentSubCategory,
    currentTimeframe,
    defaultSource,
    defaultTimeframe,
    endDate,
    getCategory,
    getSource,
    getSubCategory,
    getTimeframe,
    handleActiveStateChange,
    handleSourceCategoryChange,
    isActive,
    previousPath,
    setPreviousPath: (value) => dispatch(setPreviousPath(value)),
    sources: currentSources,
    sourceCategories,
    subPublishers,
    startDate,
    subCategories,
    timeframes: frequencies,
    peerreviewed,
  };
}
